import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, 
  ChevronRight, 
  Award, 
  CheckCircle, 
  XCircle,
  RefreshCcw,
  HelpCircle,
  AlertTriangle,
  FileText,
  ArrowRight,
  Info,
  Users,
  Settings,
  RotateCw,
  Filter,
  Sparkles,
  Clock
} from 'lucide-react';
import { ProcessItem } from '@/types';
import { 
  EnhancedQuizQuestion, 
  QuizDifficulty
} from '@/types/quiz';
import { generateEnhancedQuizQuestions } from '@/utils/enhancedQuizUtils';

// Quiz-komponent
const EnhancedQuizModal = ({ 
  isOpen, 
  onClose, 
  processData 
}) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [difficulty, setDifficulty] = useState('mixed');
  const [questionCount, setQuestionCount] = useState(10);
  const [timeSpent, setTimeSpent] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // Statistik för quizet
  const [stats, setStats] = useState({
    questionsAnswered: 0,
    correctAnswers: 0,
    wrongAnswers: 0,
    byType: {}
  });
  
  // Generera frågor från processData när modalen öppnas
  useEffect(() => {
    if (isOpen && processData.length > 0) {
      setLoading(true);
      // Återställ quiz-tillstånd när modalen öppnas på nytt
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setScore(0);
      setQuizCompleted(false);
      setQuizStarted(false);
      setTimeSpent(0);
      setTimerActive(false);
      setStats({
        questionsAnswered: 0,
        correctAnswers: 0,
        wrongAnswers: 0,
        byType: {}
      });
      
      try {
        // Generera quiz-frågor med förbättrad utils-funktion
        const generatedQuestions = generateEnhancedQuizQuestions(
          processData, 
          questionCount,
          difficulty
        );
        setQuestions(generatedQuestions);
      } catch (error) {
        console.error("Error generating quiz questions:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [isOpen, processData, questionCount, difficulty]);
  
  // Timer för quizet
  useEffect(() => {
    let interval;
    
    if (timerActive) {
      interval = setInterval(() => {
        setTimeSpent(prev => prev + 1);
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive]);
  
  // Starta quizet
  const startQuiz = () => {
    setQuizStarted(true);
    setTimerActive(true);
  };
  
  // Hantera när användaren väljer ett svar
  const handleAnswerSelect = (answerIndex) => {
    if (isAnswered) return; // Förhindra att välja svar efter att ha svarat
    
    setSelectedAnswer(answerIndex);
    setIsAnswered(true);
    
    const currentQuestionObj = questions[currentQuestion];
    const isCorrect = answerIndex === currentQuestionObj.correctAnswerIndex;
    
    // Uppdatera poäng
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    
    // Uppdatera statistik
    setStats(prev => {
      const newStats = { ...prev };
      newStats.questionsAnswered++;
      
      if (isCorrect) {
        newStats.correctAnswers++;
      } else {
        newStats.wrongAnswers++;
      }
      
      // Spåra per frågetyp
      const type = currentQuestionObj.type;
      if (!newStats.byType[type]) {
        newStats.byType[type] = { total: 0, correct: 0 };
      }
      
      newStats.byType[type].total++;
      if (isCorrect) {
        newStats.byType[type].correct++;
      }
      
      return newStats;
    });
  };
  
  // Gå till nästa fråga
  const goToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      // Quiz avslutat
      setQuizCompleted(true);
      setTimerActive(false);
    }
  };
  
  // Starta om quizet med nya frågor
  const restartQuiz = () => {
    setLoading(true);
    
    try {
      // Generera nya frågor
      const generatedQuestions = generateEnhancedQuizQuestions(
        processData, 
        questionCount,
        difficulty
      );
      
      setQuestions(generatedQuestions);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setScore(0);
      setQuizCompleted(false);
      setTimeSpent(0);
      setTimerActive(true);
      setStats({
        questionsAnswered: 0,
        correctAnswers: 0,
        wrongAnswers: 0,
        byType: {}
      });
    } catch (error) {
      console.error("Error generating new quiz questions:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Hantera inställningsändring för svårighetsnivå
  const handleDifficultyChange = (newDifficulty) => {
    setDifficulty(newDifficulty);
  };
  
  // Hantera inställningsändring för antal frågor
  const handleQuestionCountChange = (count) => {
    setQuestionCount(count);
  };
  
  // Få meddelande baserat på poäng
  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    
    if (percentage >= 90) {
      return "Utmärkt! Du är en processexpert!";
    } else if (percentage >= 70) {
      return "Bra jobbat! Du har god kunskap om processerna.";
    } else if (percentage >= 50) {
      return "Inte illa! Du har grundläggande kunskap om processerna.";
    } else if (percentage >= 30) {
      return "Du har viss kunskap om processerna, men det finns utrymme för förbättring.";
    } else {
      return "Du behöver lära dig mer om processerna. Prova igen!";
    }
  };
  
  // Få färg för poängmeddelande
  const getScoreColor = () => {
    const percentage = (score / questions.length) * 100;
    
    if (percentage >= 90) {
      return "text-green-400";
    } else if (percentage >= 70) {
      return "text-blue-400";
    } else if (percentage >= 50) {
      return "text-orange-400";
    } else if (percentage >= 30) {
      return "text-yellow-400";
    } else {
      return "text-red-400";
    }
  };
  
  // Formatera tid till MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Funktion för att rendera frågetypsikon
  const renderQuestionTypeIcon = (question) => {
    const iconClassName = "h-5 w-5 mr-2";
    
    switch (question.type) {
      case 'main-process':
        return <ChevronRight className={`${iconClassName} text-orange-300`} />;
      case 'sub-process':
        return <ChevronRight className={`${iconClassName} text-blue-300`} />;
      case 'moment':
        return <CheckCircle className={`${iconClassName} text-green-300`} />;
      case 'participant':
        return <Users className={`${iconClassName} text-purple-300`} />;
      case 'document':
        return <FileText className={`${iconClassName} text-yellow-300`} />;
      case 'important':
        return <AlertTriangle className={`${iconClassName} text-red-300`} />;
      case 'detailed-description':
        return <Info className={`${iconClassName} text-pink-300`} />;
      case 'fill-in-blank':
        return <HelpCircle className={`${iconClassName} text-indigo-300`} />;
      case 'sequence':
        return <ArrowRight className={`${iconClassName} text-teal-300`} />;
      case 'relation':
        return <Sparkles className={`${iconClassName} text-amber-300`} />;
      default:
        return <BookOpen className={`${iconClassName} text-gray-300`} />;
    }
  };
  
  // Få beskrivande namn för frågetyp
  const getQuestionTypeName = (type) => {
    switch (type) {
      case 'main-process': return 'Huvudprocesser';
      case 'sub-process': return 'Delprocesser';
      case 'moment': return 'Moment';
      case 'participant': return 'Deltagare och roller';
      case 'document': return 'Dokument';
      case 'important': return 'Viktiga markeringar';
      case 'detailed-description': return 'Detaljerade beskrivningar';
      case 'fill-in-blank': return 'Fyll i luckan';
      case 'sequence': return 'Processordning';
      case 'relation': return 'Processrelationer';
      default: return 'Övriga frågor';
    }
  };
  
  // Få svårighetsgrad som text
  const getDifficultyText = (difficultyLevel) => {
    if (!difficultyLevel) return '';
    
    switch (difficultyLevel) {
      case 'easy': return 'Lätt';
      case 'medium': return 'Medel';
      case 'hard': return 'Svår';
      default: return '';
    }
  };
  
  // Få färg för svårighetsgrad
  const getDifficultyColor = (difficultyLevel) => {
    if (!difficultyLevel) return '';
    
    switch (difficultyLevel) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-orange-400';
      case 'hard': return 'text-red-400';
      default: return '';
    }
  };
  
  // Renderar startskärmen
  const renderStartScreen = () => (
    <div className="text-center py-8">
      <div className="flex justify-center mb-6">
        <div className="bg-orange-500/20 p-4 rounded-full">
          <BookOpen className="h-12 w-12 text-orange-300" />
        </div>
      </div>
      
      <h2 className="text-2xl font-medium text-white mb-4">Processquiz</h2>
      
      <p className="text-gray-300 mb-6 max-w-md mx-auto">
        Testa dina kunskaper om processerna! Du kommer att få svara på {questions.length} frågor om huvudprocesser, delprocesser, moment, dokument, roller och mycket mer.
      </p>
      
      {questions.length > 0 ? (
        <div className="flex flex-col gap-4 items-center">
          <button 
            onClick={startQuiz}
            className="bg-orange-500 hover:bg-orange-600 text-gray-900 px-8 py-4 rounded-lg font-medium"
          >
            Starta Quiz <ChevronRight className="ml-2 h-4 w-4 inline" />
          </button>
          
          <button 
            onClick={() => setSettingsOpen(true)}
            className="text-gray-300 border border-gray-700 hover:bg-gray-700/50 px-4 py-2 rounded-lg"
          >
            <Settings className="mr-2 h-4 w-4 inline" />
            Anpassa quiz
          </button>
        </div>
      ) : (
        <div className="text-gray-400 bg-gray-800/50 rounded-lg p-4 max-w-md mx-auto">
          Det gick inte att skapa några quizfrågor. Kontrollera att processdata innehåller tillräckligt med information.
        </div>
      )}
    </div>
  );
  
  // Rendera resultatskärmen
  const renderResultScreen = () => (
    <div className="text-center py-8">
      <div className="flex justify-center mb-6">
        <div className="bg-orange-500/20 p-4 rounded-full">
          <Award className="h-12 w-12 text-orange-300" />
        </div>
      </div>
      
      <h2 className="text-2xl font-medium text-white mb-4">Quiz avklarat!</h2>
      
      <div className="flex justify-center items-center gap-4 mb-6">
        <div className={`text-4xl font-bold ${getScoreColor()}`}>
          {score} / {questions.length}
        </div>
        <div className="text-gray-400 flex items-center">
          <Clock className="h-4 w-4 mr-1" />
          {formatTime(timeSpent)}
        </div>
      </div>
      
      <p className="text-xl text-gray-300 mb-8">
        {getScoreMessage()}
      </p>
      
      {/* Statistik per frågetyp */}
      <div className="bg-gray-800/50 rounded-lg p-4 max-w-md mx-auto mb-6">
        <h3 className="text-white font-medium mb-4">Statistik per frågetyp</h3>
        
        <div className="space-y-3">
          {Object.entries(stats.byType).map(([type, data]) => (
            <div key={type} className="flex items-center justify-between">
              <div className="flex items-center">
                {renderQuestionTypeIcon({ type })}
                <span className="text-gray-300">{getQuestionTypeName(type)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={data.correct === data.total ? "text-green-400" : "text-orange-300"}>
                  {data.correct} / {data.total}
                </span>
                <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500"
                    style={{ width: `${(data.correct / data.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex justify-center gap-4">
        <button 
          onClick={restartQuiz}
          className="bg-orange-500 hover:bg-orange-600 text-gray-900 px-6 py-2 rounded-lg font-medium"
        >
          <RefreshCcw className="mr-2 h-4 w-4 inline" />
          Nytt quiz
        </button>
        
        <button 
          onClick={() => setSettingsOpen(true)}
          className="text-gray-300 border border-gray-700 hover:bg-gray-700/50 px-4 py-2 rounded-lg"
        >
          <Settings className="mr-2 h-4 w-4 inline" />
          Anpassa
        </button>
      </div>
    </div>
  );
  
  // Rendera aktiv fråga
  const renderQuestion = () => {
    if (questions.length === 0 || currentQuestion >= questions.length) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-300">Inga frågor tillgängliga</p>
        </div>
      );
    }

    const question = questions[currentQuestion];

    return (
      <div className="py-4">
        {/* Quiz-header med navigationsinformation och timer */}
        <div className="flex flex-wrap justify-between items-center mb-6 gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-gray-800/80 px-3 py-1 rounded-full text-sm text-gray-300">
              Fråga {currentQuestion + 1} av {questions.length}
            </div>
            <div className="text-sm text-gray-400 flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatTime(timeSpent)}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Svårighetsnivå */}
            {question.difficulty && (
              <div className={`text-xs font-medium ${getDifficultyColor(question.difficulty)} bg-gray-800/80 px-2 py-1 rounded-full`}>
                {getDifficultyText(question.difficulty)}
              </div>
            )}
            
            <div className="text-sm text-gray-400">
              Poäng: {score}
            </div>
          </div>
        </div>
        
        {/* Fråga */}
        <div className="mb-6">
          <div className="flex items-center mb-3">
            {renderQuestionTypeIcon(question)}
            <h3 className="text-lg font-medium text-white">
              {question.questionText}
            </h3>
          </div>
          
          {/* Om frågan har kontext visas den här */}
          {question.contextText && (
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4 text-gray-300 border-l-4 border-orange-500/50">
              {question.contextText}
            </div>
          )}
        </div>
        
        {/* Svarsalternativ */}
        <div className="space-y-3 mb-6">
          {question.answers.map((answer, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={isAnswered}
              className={`w-full text-left p-4 rounded-lg border transition-colors ${
                isAnswered && index === selectedAnswer 
                  ? index === question.correctAnswerIndex
                    ? "bg-green-400/20 border-green-500/50 text-green-300"
                    : "bg-red-400/20 border-red-500/50 text-red-300"
                  : isAnswered && index === question.correctAnswerIndex
                  ? "bg-green-400/20 border-green-500/50 text-green-300"
                  : "bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50 text-white"
              }`}
            >
              <div className="flex items-center">
                <div className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full mr-3 border text-xs font-medium
                  ${isAnswered ? 'border-transparent' : 'border-gray-600'}`}>
                  {String.fromCharCode(65 + index)}
                </div>
                <div className="flex-1">
                  {answer.text}
                </div>
                {isAnswered && index === question.correctAnswerIndex && (
                  <CheckCircle className="h-5 w-5 text-green-400 ml-2" />
                )}
                {isAnswered && index === selectedAnswer && index !== question.correctAnswerIndex && (
                  <XCircle className="h-5 w-5 text-red-400 ml-2" />
                )}
              </div>
            </button>
          ))}
        </div>
        
        {/* Förklaring efter svar */}
        <AnimatePresence>
          {isAnswered && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className={`p-4 rounded-lg mb-6 ${
                selectedAnswer === question.correctAnswerIndex
                  ? "bg-green-500/10 border border-green-500/30"
                  : "bg-red-500/10 border border-red-500/30"
              }`}
            >
              <div className="flex items-start gap-2">
                {selectedAnswer === question.correctAnswerIndex ? (
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400 mt-0.5" />
                )}
                <div>
                  <h4 className={`font-medium ${
                    selectedAnswer === question.correctAnswerIndex
                      ? "text-green-400"
                      : "text-red-400"
                  }`}>
                    {selectedAnswer === question.correctAnswerIndex
                      ? "Rätt svar!"
                      : "Fel svar"
                    }
                  </h4>
                  <p className="text-gray-300 text-sm mt-1">
                    {question.explanation}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Navigeringsknappar */}
        <div className="flex justify-between">
          <button
            onClick={() => setSettingsOpen(true)}
            className="text-gray-400 border border-gray-700 hover:bg-gray-700/50 px-3 py-1 rounded-lg text-sm"
          >
            <Settings className="h-4 w-4 mr-2 inline" />
            Inställningar
          </button>
          
          {isAnswered && (
            <button 
              onClick={goToNextQuestion}
              className="bg-orange-500 hover:bg-orange-600 text-gray-900 px-4 py-2 rounded-lg"
            >
              {currentQuestion < questions.length - 1 ? (
                <>Nästa fråga <ChevronRight className="ml-2 h-4 w-4 inline" /></>
              ) : (
                <>Visa resultat <ChevronRight className="ml-2 h-4 w-4 inline" /></>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };
  
  // Renderar inställningspanelen
  const renderSettingsPanel = () => (
    <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-white flex items-center gap-2">
          <Settings className="h-5 w-5 text-orange-300" />
          <span>Quizinställningar</span>
        </h3>
        <button 
          onClick={() => setSettingsOpen(false)}
          className="text-gray-400 hover:text-white p-2 rounded-full"
        >
          Stäng
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Svårighetsnivå */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-3">Svårighetsnivå</h4>
          <div className="flex flex-col space-y-2">
            {(['easy', 'medium', 'hard', 'mixed']).map((level) => (
              <button
                key={level}
                onClick={() => handleDifficultyChange(level)}
                className={`flex items-center justify-start px-3 py-2 rounded-lg ${
                  difficulty === level 
                    ? "bg-orange-500 hover:bg-orange-600 text-white" 
                    : "bg-gray-800/50 hover:bg-gray-700/70 text-gray-300 border border-gray-700"
                }`}
              >
                {level === 'easy' && <CheckCircle className="h-4 w-4 mr-2 text-green-400" />}
                {level === 'medium' && <AlertTriangle className="h-4 w-4 mr-2 text-orange-400" />}
                {level === 'hard' && <XCircle className="h-4 w-4 mr-2 text-red-400" />}
                {level === 'mixed' && <Sparkles className="h-4 w-4 mr-2 text-blue-400" />}
                
                {level === 'easy' && "Lätt"}
                {level === 'medium' && "Medel"}
                {level === 'hard' && "Svår"}
                {level === 'mixed' && "Blandad"}
              </button>
            ))}
          </div>
        </div>
        
        {/* Antal frågor */}
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-3">Antal frågor</h4>
          <div className="flex flex-col space-y-2">
            {[5, 10, 15, 20].map((count) => (
              <button
                key={count}
                onClick={() => handleQuestionCountChange(count)}
                className={`flex items-center justify-start px-3 py-2 rounded-lg ${
                  questionCount === count 
                    ? "bg-orange-500 hover:bg-orange-600 text-white" 
                    : "bg-gray-800/50 hover:bg-gray-700/70 text-gray-300 border border-gray-700"
                }`}
              >
                {count} frågor
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="mt-6 flex justify-end">
        <button 
          onClick={() => {
            setLoading(true);
            setTimeout(() => {
              try {
                const generatedQuestions = generateEnhancedQuizQuestions(
                  processData, 
                  questionCount,
                  difficulty
                );
                setQuestions(generatedQuestions);
              } catch (error) {
                console.error("Error generating quiz questions:", error);
              } finally {
                setLoading(false);
                setSettingsOpen(false);
              }
            }, 100);
          }}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
        >
          <RotateCw className="h-4 w-4 mr-2 inline" />
          Uppdatera quiz
        </button>
      </div>
    </div>
  );
  
  // Laddar-vy när frågor genereras
  const renderLoadingScreen = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative w-16 h-16 mb-6">
        <motion.div
          className="absolute inset-0 rounded-full border-t-2 border-orange-300"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-2 rounded-full border-t-2 border-blue-300"
          animate={{ rotate: -180 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />
      </div>
      <p className="text-white text-lg mb-2">Laddar processer...</p>
      <p className="text-gray-400 text-sm">Genererar frågor från processdata</p>
    </div>
  );

  // Om modalen inte är öppen, visa ingenting
  if (!isOpen) return null;

  // Rendera modal med relevant innehåll
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-medium text-white flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-orange-300" />
            <span>Processquiz</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-2 rounded-full"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mt-2 text-gray-300">
          {loading ? renderLoadingScreen() : (
            settingsOpen ? renderSettingsPanel() : (
              !quizStarted ? renderStartScreen() : (
                quizCompleted ? renderResultScreen() : renderQuestion()
              )
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedQuizModal;